import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment.prod';
import { Observable, firstValueFrom, BehaviorSubject } from 'rxjs';
import { discountCodePostDataInterface, updateCartPostDataInterface } from 'src/_lib/interfaces/request-interfaces/cart.interface';
import { generateOrderKeyDataInterface } from 'src/_lib/interfaces/request-interfaces/product.interface';
import { AppConstants } from 'src/app/app.constants';
import { Router } from '@angular/router';
import { BrowserService } from 'src/_lib/service/browser.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private endpoint: string = environment.apiUrl;
  cartData = new EventEmitter<[] | []>();
  private validDiscountCode = '';
  firstObj: number = this.appConstants.firstObj;
  private orderItemList: { [product_variation_key: number]: number } = {};
  private selectProductSkuFlag = new BehaviorSubject<boolean>(false);
  selectProductSkuFlag$ = this.selectProductSkuFlag.asObservable();

  constructor(
    private http: HttpClient,
    private appConstants: AppConstants,
    private router: Router,
    private browser: BrowserService
  ) {
    this.orderItemList = {};
    if (this.browser.isBrowser) {
      const data = this.browser.localStorage?.getItem('orderItemsList');
      this.orderItemList = (data ? JSON.parse(data) : null) ?? {};
    }
  }

  cartList(order_key: number): Observable<any> {
    return this.http.get(this.endpoint + 'orders/' + order_key);
  }

  removeToCart(orderData: {
    order_key: number,
    order_item_key: number,
    product_variation_key: number,
    qty: number,
    price: number,
    is_custom: boolean,
  }) {
    return this.http.request('DELETE', this.endpoint + 'orders/removeitem', {
      body: orderData
    });
  }

  cart(order_key: number) {
    const cacheBuster = new Date().getTime();
    return this.http.get(`${this.endpoint}orders/${order_key}?cb=${cacheBuster}`);
  }

  isValidDiscountCode(code: string): boolean {
    return code === this.validDiscountCode;
  }

  applydiscountCode(data: discountCodePostDataInterface) {
    const url = `${this.endpoint}discounts`;
    return this.http.post<any>(url, data);
  }

  removeCartDiscount(data: discountCodePostDataInterface) {
    const url = `${this.endpoint}discounts`;
    return this.http.post<any>(url, data);
  }

  updateCart(data: updateCartPostDataInterface) {
    const url = `${this.endpoint}orders`;
    return this.http.put(url, data);
  }

  likeProductData(order_key: number): Observable<any> {
    return this.http.get(`${this.endpoint}product-you-may-like/${order_key}`);
  }

  ProductPromotionData(): Observable<any> {
    return this.http.get(`${this.endpoint}cart-promotion`);
  }

  addOrder(orderData: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(this.endpoint + 'orders', orderData));
  }

  postCorporateCustomer(data: { email_address: string; type: string }): Observable<any> {
    return this.http.post(this.endpoint + 'corporate-customer', data);
  }

  militaryDiscount(formData: any): Observable<any> {
    return this.http.post<any>(this.endpoint + 'military-discount', formData);
  }

  getCartVerify(data): Observable<any> {
    return this.http.post<any>(`${this.endpoint}verifycart`, data);
  }

  verifyCart(order_key: number) {
    return this.http.get(this.endpoint + 'verifycart/' + order_key);
  }

  async generateOrderKey(data: generateOrderKeyDataInterface): Promise<null> {
    return new Promise((resolve, reject) => {
      this.http.post(this.endpoint + 'orders', data).subscribe(
        {
          next: (response) => {
            if (response[this.firstObj]?.success == true) {
              const orderID = Number(response[this.firstObj]?.data?.order_key) || null;
             if (this.browser.isBrowser) {
             this.browser.localStorage?.setItem('orderId', String(orderID));
              }
              resolve(null);
            }
            else {
              resolve(null);
            }
          },
          error: (error: Error) => {
            console.error('Custom Error: Unexpected Error in order key generation:', error);
            reject(null);
          }
        }
      );
    })
  }

  async addToCart(
    product: any | { product_key: number, product_variation_key: number, sku: string },
    order_key: number,
    qty: number,
    price: number = parseFloat(product.customer_price),
    promotion_key: number = product.promotion_key,
    cart_promotion: boolean,
    product_option_key: string = '',
    is_custom: boolean,
    color_key: number | null = null,
    size_key: number | null = null
  ) {
    const data = {
      order_key: order_key || "",
      product_key: Number(product.product_key),
      product_variation_key: parseInt(product.product_variation_key),
      sku: String(product.sku),
      qty: +qty,
      price: price,
      promotion_key: promotion_key,
      cart_promotion: cart_promotion,
      product_option_key: product_option_key || '',
      is_custom: is_custom,
      color_key: color_key,
      size_key: size_key
    };
    await this.generateOrderKey(data);
    this.setOrderItemsList(+product.product_variation_key, qty);
    this.setLocalStorageOrderItemsList();
    if (this.browser.isBrowser) {
  const itemCount = +(this.browser.localStorage?.getItem('ItemCount') || 0);
  this.browser.localStorage?.setItem('ItemCount', String(itemCount + qty));
}
    this.router.navigate(['/cart']);
  }

  async addOptionsToCart(
    product: any,
    order_key: number,
    qty: number,
    price: number,
    promotion_key: any,
    product_option_key: string,
    color_key: number | null,
    size_key: number | null,
    optionProducts: { variation_key: number; sku: string; price: number }[]
  ) {
    const mainProduct = {
      order_key: order_key || null,
      product_key: Number(product.product_key),
      product_variation_key: Number(product.product_variation_key),
      sku: String(product.sku),
      qty: qty,
      price: price,
      promotion_key: promotion_key || null,
      cart_promotion: false,
      product_option_key: product_option_key,
      is_custom: false,
      color_key: color_key,
      size_key: size_key
    };
    const optionItems = optionProducts
      .filter(opt => opt.variation_key && opt.variation_key !== 0)
      .map(opt => ({
        order_key: order_key || null,
        product_key: '',
        product_variation_key: opt.variation_key,
        sku: opt.sku,
        qty: 1,
        price: opt.price,
        promotion_key: null,
        cart_promotion: false,
        product_option_key: '',
        is_custom: false
      }));
    const payload = [mainProduct, ...optionItems];
    await this.generateOptionsOrderKey(payload);
    this.setOrderItemsList(+product.product_variation_key, qty);
    this.setLocalStorageOrderItemsList();
    if (this.browser.isBrowser) {
  const itemCount = +(this.browser.localStorage?.getItem('ItemCount') || 0);
  this.browser.localStorage?.setItem('ItemCount', String(itemCount + qty));
}
    this.router.navigate(['/cart']);
  }

  async generateOptionsOrderKey(data: any): Promise<null> {
    return new Promise((resolve, reject) => {
      const apiEndpoint = this.endpoint + 'orders/create-options-order';
      this.http.post(apiEndpoint, data).subscribe({
        next: (response: any) => {
          if (response[this.firstObj]?.success === true) {
            const orderID = Number(response[this.firstObj]?.data?.order_key) || null;
            if (this.browser.isBrowser) {
              this.browser.localStorage?.setItem('orderId', String(orderID));
            }
            resolve(null);
          } else {
            reject(null);
          }
        },
        error: (error: Error) => {
          console.error('Error in options order key generation:', error);
          reject(null);
        }
      });
    });
  }

  initializeOrderItemsList() {
    this.orderItemList = {};
  }

  setOrderItemsList(product_variation_key: number, numberOfItems: number) {
    this.orderItemList[product_variation_key] = numberOfItems;
  }

  removeOrderFromOrderItemsList(product_variation_key: number) {
    delete this.orderItemList[product_variation_key];
  }

  setLocalStorageOrderItemsList() {
    if (this.browser.isBrowser) {
  this.browser.localStorage?.setItem(
    'orderItemsList',
    JSON.stringify(this.getOrderItemsList())
  );
}
  }

  getOrderItemsList() {
    const items = Object.keys(this.orderItemList);
    return items?.length > 0 ? this.orderItemList : null;
  }

  updateFreeShippingStatus(orderKey: number, isFreeShipping: boolean): Observable<any> {
    const payload = {
      order_key: orderKey,
      free_shipping: isFreeShipping
    };
    return this.http.put(this.endpoint + 'free-shipping', payload);
  }

  setSelectProductSkuFlag(value: boolean) {
    this.selectProductSkuFlag.next(value);
  }

  getCartShippingType(): string | null {
   return this.browser.localStorage?.getItem('cartShippingType') ?? null; // 'freight' | 'standard' | null
  }

  setCartShippingType(isFreight: boolean) {
    if (this.browser.isBrowser) {
      this.browser.localStorage?.setItem('cartShippingType', isFreight ? 'freight' : 'standard');
    }
  }

  clearCartShippingType() {
    if (this.browser.isBrowser) {
      this.browser.localStorage?.removeItem('cartShippingType');
    }
  }
}